<template>
  <vd-error
    v-if="!queryLoading && notFound"
    status="404"
    message="The metric you are looking for does not exist"
    :back-link="backLink"
  />
  <vd-error
    v-else-if="!queryLoading && featureId && !inWIPVersion && !preview"
    status="405"
    message="You can not edit the old version of a metric"
    :back-link="backLink"
  />
  <vd-error
    v-else-if="!queryLoading && !featureId && !inWIPVersion"
    status="405"
    message="You can not link a metric in an old version"
    :back-link="backLink"
  />
  <div v-else class="feature-form-page">
    <vd-page-form
      class="feature-form"
      :preview="preview"
      :title="pageTitle"
      :back-link="backLink"
      :edit-link="editLink"
      :form="form"
      :hide-actions="!inWIPVersion || !canWriteFeatures"
      :save-callback="saveForm"
      :save-loading="saveLoading || createLoading"
      :loading="queryLoading"
      :allow-delete="!!featureId && canWriteFeatures"
      @delete="onDelete"
    >
      <VdFormField
        v-model="form.name"
        label="Metric name"
        prop="name"
        type="text"
        required
        show-word-limit
        max-length="40"
        :disabled="!!featureId"
      />
      <VdFormField
        v-model="form.id"
        prop="id"
        type="text"
        label="VoodooSauce Keyword"
        required
        :disabled="!!featureId"
        show-word-limit
        max-length="40"
        :error="technicalNameErrorMessage"
        @input="technicalNameErrorMessage = ''"
      />
      <VdFormField
        v-model="form.description"
        prop="description"
        type="textarea"
        label="Description"
        :disabled="!!featureId && isSystemMetric"
        show-word-limit
        max-length="150"
        :auto-size="{ minRows: 3, maxRows: 4 }"
      />
      <VdFormField label="Category" prop="category" required>
        <VdSelect v-model="form.category" :choices="featureCategoryChoices" :disabled="disabled" />
      </VdFormField>
      <div class="data-type-fields">
        <VdFormField label="Data type" prop="type" required>
          <VdSelect v-model="form.type" :choices="featureTypeChoices" :disabled="disabled" />
        </VdFormField>
        <VdFormField
          v-if="isSelectType"
          label="Available values"
          prop="availableValues"
          class="available-values-field"
          :rules="[{ required: true, message: 'you must at least enter a value' }]"
        >
          <MultiSelectWithChips
            v-model="form.availableValues"
            :disabled="disabled"
            :placeholder="form.availableValues && form.availableValues.length > 0 ? '' : 'Separate values with a comma'"
            @value-change="handleValueChange"
          />
        </VdFormField>
      </div>
    </vd-page-form>

    <!-- warning delete dialog -->
    <vd-dialog
      :visible="displayDeleteWarning"
      title="This feature can't be deleted"
      type="error"
      @close="closeDeleteWarning"
    >
      <span>
        You can't delete this feature as it is used
        <strong>in the following segments:</strong>
      </span>
      <ul class="delete-warning-list">
        <li v-for="segment in deleteWarningDetails.segments" :key="segment">
          {{ segment }}
        </li>
      </ul>
      <template #footer>
        <vd-button type="secondary" skin="blue" @click="closeDeleteWarning">I understand</vd-button>
      </template>
    </vd-dialog>
  </div>
</template>

<script setup lang="ts">
/**
 * This page displays a feature form.
 */

import { VdFormField, VdSelect } from "@voodoo.io/voodoo-ui";
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";

import MultiSelectWithChips from "@/components/MultiSelectWithChips/MultiSelectWithChips.vue";
import {
  FeatureFormInput,
  FeatureType,
  featureTypeChoices,
  useDeleteFeatureMutation,
  useFeatureFormPageCreateMutation,
  useFeatureFormPageQuery,
  useFeatureFormPageSaveMutation,
  useMutationResponse,
} from "@/graphql";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";
import isWithUnderscoreWithoutSpaces from "@/utils/isWithUnderscoreWithoutSpaces";

const props = defineProps({
  /**
   * The game id (the features listed are related to a specific game)
   */
  gameId: {
    type: String,
    required: true,
  },

  /**
   * The version id (the features listed are related to a specific version)
   */
  versionId: {
    type: String,
    required: true,
  },

  /**
   * If the featureId is present, update the feature, create it otherwise.
   */
  featureId: {
    type: String,
    default: null,
    required: false,
  },

  /**
   * If true, enter in the preview mode
   */
  preview: {
    type: Boolean,
    default: false,
  },

  edit: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const { canWriteFeatures } = usePermissions();

const featureCategoryChoices = [
  { label: "Purchase Amount", value: "purchase_amount" },
  { label: "Purchase Frequency", value: "purchase_frequency" },
  { label: "Advertising Metrics", value: "advertising_metrics" },
  { label: "Engagement", value: "engagement" },
  { label: "Player Progress", value: "player_progress" },
  { label: "Technical Info", value: "technical_info" },
];

/**
 * Define the form
 */
const form = reactive<FeatureFormInput>({
  id: "", // technicalName
  name: "",
  type: FeatureType.String,
  description: "",
  availableValues: [],
  source: "",
  category: "",
  windowUnit: null,
  windowSize: null,
  measurementMethod: null,
  iapFilterType: null,
  iaps: null,
});

const technicalNameErrorMessage = ref("");

/**
 * Try to fetch the feature from its id.
 * If we have a feature, update the form title and fill the form
 */
const { result, loading: queryLoading } = useFeatureFormPageQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
    featureId: props.featureId,
  },
  { fetchPolicy: "network-only" }
);

const feature = computed(() => result.value?.game?.version?.feature);
const inWIPVersion = computed(() => result.value?.game?.version?.isWip);
const notFound = computed(() => !queryLoading.value && props.featureId && !feature.value);
const isSystemMetric = computed(() => feature.value?.source === "system");
const disabled = computed(() => props.preview || (isSystemMetric.value && props.edit));

const pageTitle = computed(() => {
  if (props.featureId) {
    return (feature.value && feature.value.name) || "";
  }
  return "New metric";
});

watch(feature, (value) => {
  if (value) {
    form.id = value.id;
    form.name = value.name;
    form.type = value.type;
    form.description = value.description ?? "";
    form.availableValues = value.availableValues ?? [];
    form.source = value.source;
    form.category = value.category ?? "";
  }
});

const isSelectType = computed(() => form.type === FeatureType.Select);

/**
 * Define the location to return after save/deletion/cancel
 */
const backLink = { name: links.games.features.list, params: { gameId: props.gameId, versionId: props.versionId } };

const editLink = {
  name: links.games.features.edit,
  params: { gameId: props.gameId, versionId: props.versionId, featureId: props.featureId },
};

/**
 * Save the feature
 */
const { mutate: saveFeature, loading: saveLoading } = useFeatureFormPageSaveMutation({});

const onFeatureSave = async () => {
  const response = await saveFeature({
    gameId: props.gameId,
    versionId: props.versionId,
    featureId: props.featureId,
    input: {
      ...form,
      availableValues: form.type === FeatureType.Select ? form.availableValues : [],
    },
  });

  const saveResult = useMutationResponse(response, (resp) => resp?.saveFeature);

  if (saveResult?.success) {
    router.push(backLink);
  } else if (saveResult?.isConflict) {
    technicalNameErrorMessage.value = "The technical name is already taken";
  }
};

/**
 * Creature the feature
 */
const { mutate: createFeature, loading: createLoading } = useFeatureFormPageCreateMutation({});

const onFeatureCreate = async () => {
  if (isWithUnderscoreWithoutSpaces(form.id || "")) {
    technicalNameErrorMessage.value = "The technical name should not include spaces and special characters";
    return;
  }

  const response = await createFeature({
    gameId: props.gameId,
    versionId: props.versionId,
    input: {
      ...form,
      availableValues: form.type === FeatureType.Select ? form.availableValues : [],
      source: "app_custom",
    },
  });

  const saveResult = useMutationResponse(response, (resp) => resp?.createFeature);

  if (saveResult?.success) {
    router.push(backLink);
  }
};

const saveForm = async () => {
  if (props.featureId) {
    await onFeatureSave();
  } else {
    await onFeatureCreate();
  }
};

const handleValueChange = (value: string[]) => {
  const newvalue = value
    .map((item) => item.split(","))
    .reduce((acc, val) => acc.concat(val), [])
    .map((item) => item.trim())
    .filter((item) => item !== "");

  const uniqueValues = Array.from(new Set(newvalue));
  form.availableValues = uniqueValues;
};

const displayDeleteWarning = ref(false);

const deleteWarningDetails = reactive<{ segments: string[] }>({
  segments: [],
});

const closeDeleteWarning = () => {
  displayDeleteWarning.value = false;
};

/**
 * Delete the feature
 */
const { mutate: deleteFeature } = useDeleteFeatureMutation({});

const onDelete = async () => {
  if (!props.featureId) {
    return;
  }

  const response = await deleteFeature({
    gameId: props.gameId,
    versionId: props.versionId,
    featureId: props.featureId,
  });

  const deleteResult = useMutationResponse(response, (resp) => resp?.deleteFeature);

  if (deleteResult?.success) {
    router.push(backLink);
  } else if (deleteResult?.isCurrentlyUsed) {
    deleteWarningDetails.segments = deleteResult?.usedInSegments ?? [];
    displayDeleteWarning.value = true;
  }
};
</script>

<style lang="scss">
.feature-form {
  .vd-form__content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 40px;

    .data-type-fields {
      grid-column: span 2;

      display: flex;
      align-items: center;

      .available-values-field {
        width: 100%;
        margin-left: size("ML");

        .vd-input .el-select {
          width: 100%;
        }
      }
    }

    .vd-form-field {
      &:nth-child(3),
      :nth-child(4) {
        grid-column: span 2;
      }
    }
  }
}
</style>
